// frontend/src/components/AnnouncementCreateModal.js

import React, { useEffect, useState } from "react";
import { api } from "api";
import { Check, X } from "phosphor-react";

export default function AnnouncementCreateModal({
  groupId,
  open,
  onClose,
  onCreated,
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [pinned, setPinned] = useState(false);
  const [saving, setSaving] = useState(false);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const onSave = async (e) => {
    e?.preventDefault?.();

    const t = (title || "").trim();
    const b = (body || "").trim();
    if (!t && !b) return;

    // Combine into single backend "body" field:
    // First line = title (if present), blank line, then body.
    const combined = t && b ? `${t}\n\n${b}` : t ? t : b;

    try {
      setSaving(true);

      await api.post(`/groups/${groupId}/announcements`, {
        body: combined,
        pinned: !!pinned,
      });

      setSaving(false);
      onCreated?.();
      onClose?.();
      setTitle("");
      setBody("");
      setPinned(false);
    } catch {
      setSaving(false);
    }
  };

  const canSave = title.trim() || body.trim();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative z-10 w-full max-w-lg bg-white rounded-2xl shadow-[0_8px_0px_rgba(37,99,235,0.96)] overflow-visible">
        <div className="flex items-center justify-between px-5 pt-4">
          <h3 className="text-lg text-blue-700 font-semibold">Create Announcement</h3>

          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <form onSubmit={onSave} className="px-5 pb-4 pt-2 space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1 ml-3">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border rounded-2xl px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              placeholder="Announcement title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 ml-3">
              Body
            </label>
            <textarea
              rows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="w-full border rounded-2xl p-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              placeholder="Write your announcement."
            />
          </div>

          <div className="flex items-center justify-between gap-3">
            <label className="inline-flex cursor-pointer items-center gap-2 ml-2 text-sm font-medium text-blue-700 select-none">
              <input
                type="checkbox"
                checked={!!pinned}
                onChange={(e) => setPinned(e.target.checked)}
                className="peer sr-only"
              />

              <div className="flex h-4 w-4 items-center justify-center rounded-3xl bg-blue-50 border border-slate-200">
                <Check
                  size={12}
                  weight="bold"
                  className={`text-blue-500 transition-opacity ${
                    pinned ? "opacity-100" : "opacity-0"
                  }`}
                />
              </div>

              <span>Pin this announcement</span>
            </label>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="border text-sm rounded-full px-3 py-2"
                disabled={saving}
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={!canSave || saving}
                className="bg-blue-600 text-white text-sm rounded-full px-3 py-2 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Create"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}